<template>
  <main-large-dialog
    :is-visible.sync="computedIsVisible"
    :title="title"
    no-paddings-y
    no-paddings-x
    header-shadow
    :loading="loading"
    min-body-height="500px"
    :dialog-width="isLanternType ? '80%' : '30%'"
    :is-valid="isFormValid && (isLanternPatternValid || !isLanternType)"
    @agree="handleAgree"
    :agree-text="`dialog.agree${edit ? 'Save' : 'Add'}`"
    :disagree-text="readOnly ? 'dialog.disagreeClose' : 'dialog.disagree'"
    :has-agree="!readOnly"
  >
    <template #body>
      <v-row no-gutters class="pl-7">
        <v-col cols="6" class="pr-5 lantern-border" v-if="isFetched && isLanternType">
          <v-form v-model="isLanternPatternValid" ref="pattern-form">
            <ZDMLanternPatternTreeview
              :pattern="lanternObjects"
              :pole-type-features="poleTypeFeatures"
              :head-type-features="headTypeFeatures"
              :pick-arm-features="pickArmFeatures"
              :disable-edit="readOnly"
            />
          </v-form>
        </v-col>
        <v-col :cols="isLanternType ? '6' : '12'" class="pr-5" v-if="isFetched">
          <v-form v-model="isFormValid" ref="form">
            <feature-card-data :elements="formSchema" v-model="values" :is-editing-on="!readOnly" disableWidthLimit />
          </v-form>
        </v-col>
        <main-loader v-else />
      </v-row>
    </template>
  </main-large-dialog>
</template>
<script>
import { call, get } from 'vuex-pathify';

import FeatureCardData from '@/components/FeatureCardData';
import MainLargeDialog from '@/components/MainLargeDialog';
import MainLoader from '@/components/MainLoader';
import ZDMLanternPatternTreeview from '@/components/ZDMLanternPatternTreeview';

import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

export default {
  name: 'ZdmEditorFeatureDialog',
  components: { FeatureCardData, MainLargeDialog, MainLoader, ZDMLanternPatternTreeview },
  mixins: [prepareAttributesFeatureCardMixin],
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    copy: {
      type: Object,
    },
    dataSource: {
      type: String,
      required: true,
    },
    edit: {
      type: Object,
    },
    layer: {
      type: Number,
      required: true,
    },
    geometry: {
      required: true,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    sessionId: {
      type: Number,
      required: true,
    },
    tabName: {
      type: String,
      required: true,
    },
    tabSessionObjects: {
      type: Object,
      default: () => {
        return {};
      },
    },
  },
  computed: {
    dataSourcesFeatures: get('layers/dataSourcesFeatures'),
    layers: get('layers/layers'),
    layersMetadata: get('layers/metadata'),
    zdmDataDataSources: get('admin/modulesMapping@zdm_data.datasources'),
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    isLanternType() {
      return this.tabName === 'street_lamp';
    },
    title() {
      return this.readOnly
        ? this.$i18n.t(`sidebar.zdmEditor.preview${this.$_capitalize(this.tabName)}`)
        : this.$i18n.t(`sidebar.zdmEditor.${this.edit ? 'edit' : 'add'}${this.$_capitalize(this.tabName)}`);
    },

    poleTypeFeatures() {
      return this.dataSourcesFeatures['zdm_data_catalog_pole'].features.map(value => {
        return {
          id: value.id,
          ...value.properties,
        };
      });
    },
    pickArmFeatures() {
      return this.dataSourcesFeatures['zdm_data_catalog_pick_arm'].features.map(value => {
        return {
          id: value.id,
          ...value.properties,
        };
      });
    },
    headTypeFeatures() {
      return this.dataSourcesFeatures['zdm_data_catalog_head'].features.map(value => {
        return {
          id: value.id,
          ...value.properties,
        };
      });
    },
  },
  data: vm => ({
    formSchema: [],
    isFetched: true,
    isFormValid: false,
    isLanternPatternValid: false,
    loading: false,
    values: {},
    copyValuesCopy: {},
    lanternObjects: vm.prepareNewLanternObjects(),
    copyLanternObjects: vm.prepareNewLanternObjects(),
  }),
  methods: {
    addFeatureToSession: call('zdm_editor/addFeatureToSession'),
    editFeatureFromSession: call('zdm_editor/editFeatureFromSession'),
    getLayerFeature: call('layers/getLayerFeature'),
    async getCopyProperties(layerId, featureId, dataSource, treeMetadata) {
      if ((!layerId && layerId !== 0) || (!featureId && featureId !== 0) || !dataSource) {
        this.values = {};
        this.lanternObjects = this.prepareNewLanternObjects();
        this.$refs.form?.resetValidation();
        this.copyValuesCopy = {};
        this.copyLanternObjects = this.prepareNewLanternObjects();
        return;
      }
      this.isFetched = false;
      try {
        let feature;
        if (layerId === this.tabName) {
          feature = this.tabSessionObjects['insert'].find(feature => {
            return feature.metadata.id === featureId;
          });
          if (this.isLanternType) {
            this.lanternObjects = this.prepareLanternObjects(feature.metadata.street_lamp_objects);
            this.copyLanternObjects = JSON.parse(JSON.stringify(this.lanternObjects));
          }
          await new Promise(resolve => setTimeout(resolve, 1000));
        } else if (this.isLanternType) {
          feature = treeMetadata;
          this.lanternObjects = this.prepareLanternObjects(treeMetadata);
          this.copyLanternObjects = JSON.parse(JSON.stringify(this.lanternObjects));
        } else {
          feature = JSON.parse(
            JSON.stringify(await this.getLayerFeature({ layer_id: layerId, feature_id: featureId }))
          );
        }
        const idAttributeName = this.layersMetadata[this.dataSource].attributes_schema.id_name;
        const properties = this.getFlattenProperties(feature, idAttributeName);
        this.values = this.getValues(this.formSchema, properties, !this.readOnly);
        this.$refs.form?.resetValidation();
        this.copyValuesCopy = JSON.parse(JSON.stringify(this.values));
        this.isFetched = true;
      } catch {
        this.isFetched = true;
      }
    },
    handleAgree() {
      if (this.edit) {
        this.handleEditFeatureToSession();
      } else {
        this.handleAddFeatureToSession();
      }
    },
    getStreetLampObjects(objects = this.lanternObjects, geometry = this.geometry) {
      const lantern = objects[0];
      const lanternObjects = lantern.children.map(pole => {
        const poleCatalogAttributeName = this.zdmDataDataSources.pole.catalog_attribute_name;
        return {
          geometry,
          properties: { [poleCatalogAttributeName]: pole.value },
          pick_arm: pole.children.map((pickArm, i) => {
            const pickArmCatalogAttributeName = this.zdmDataDataSources.pick_arm.catalog_attribute_name;
            return {
              main: i === 0,
              geometry,
              properties: { [pickArmCatalogAttributeName]: pickArm.value, 'zdm_data_pick_arm.main': i === 0 },
              head: pickArm.children.map(head => {
                const headCatalogAttributeName = this.zdmDataDataSources.head.catalog_attribute_name;
                return { geometry, properties: { [headCatalogAttributeName]: head.value } };
              }),
            };
          }),
        };
      });
      return [{ pole: lanternObjects }];
    },
    async handleAddFeatureToSession() {
      const payload = {
        datasource: this.dataSource,
        geometry: this.geometry,
        properties: this.values,
        session_id: this.sessionId,
      };
      if (this.lanternObjects) payload.street_lamp_objects = this.getStreetLampObjects(this.lanternObjects);
      try {
        this.loading = true;
        await this.addFeatureToSession(payload);
        this.computedIsVisible = false;
        this.loading = false;
      } catch {
        this.loading = false;
      }
    },
    async handleEditFeatureToSession() {
      const data = {
        dataSource: this.dataSource,
        featureId: this.edit.metadata.id,
        sessionId: this.sessionId,
        payload: {
          properties: this.values,
        },
      };
      if (this.lanternObjects) {
        data.payload.street_lamp_objects = this.getStreetLampObjects(this.lanternObjects, {
          ...this.edit.geometry,
          crs: { properties: { name: this.$_config.defaultEpsg }, type: 'name' },
        });
      }
      try {
        this.loading = true;
        await this.editFeatureFromSession(data);
        this.computedIsVisible = false;
        this.loading = false;
      } catch {
        this.loading = false;
      }
    },
    prepareLanternObjects(objects = this.edit?.street_lamp_objects || {}) {
      return [
        {
          name: this.$i18n.t('admin.street_lamp'),
          level: 1,
          children: objects.pole.map(pole => {
            const poleCatalogAttributeName = this.zdmDataDataSources.pole.catalog_attribute_name;
            return {
              name: this.$i18n.t('admin.pole'),
              selectItems: this.poleTypeFeatures,
              level: 2,
              value: pole.properties[poleCatalogAttributeName],
              children: pole.pick_arm.map((pickArm, pickArmIndex) => {
                const pickArmCatalogAttributeName = this.zdmDataDataSources.pick_arm.catalog_attribute_name;
                return {
                  name: this.$i18n.t(`admin.${pickArm.main ? 'pick_arm_primary' : 'pick_arm_secondary'}`),
                  index: pickArmIndex,
                  selectItems: this.pickArmFeatures,
                  level: 3,
                  value: pickArm.properties[pickArmCatalogAttributeName],
                  children: pickArm.head.map((headE, headIndex) => {
                    const headCatalogAttributeName = this.zdmDataDataSources.head.catalog_attribute_name;
                    return {
                      name: this.$i18n.t('admin.light_head'),
                      parentIndex: pickArmIndex,
                      index: headIndex,
                      selectItems: this.headTypeFeatures,
                      level: 4,
                      value: headE.properties[headCatalogAttributeName],
                    };
                  }),
                };
              }),
            };
          }),
        },
      ];
    },
    prepareNewLanternObjects() {
      const patternObject = {
        pole: [undefined],
        pick_arm: [undefined],
        heads: [undefined],
      };
      return [
        {
          name: this.$i18n.t('admin.street_lamp'),
          level: 1,
          children: [
            {
              name: this.$i18n.t('admin.pole'),
              selectItems: this.poleTypeFeatures,
              value: patternObject.pole,
              level: 2,
              children: [
                {
                  name: this.$i18n.t('admin.pick_arm_primary'),
                  selectItems: this.pickArmFeatures,
                  value: patternObject.pick_arm,
                  index: 0,
                  level: 3,
                  children: patternObject.heads.map((item, index) => {
                    return {
                      value: item,
                      index,
                      level: 4,
                      name: this.$i18n.t('admin.light_head'),
                      parentIndex: 0,
                      selectItems: this.headTypeFeatures,
                    };
                  }),
                },
              ],
            },
          ],
        },
      ];
    },
  },
  watch: {
    computedIsVisible: {
      handler(nV) {
        if (nV) {
          this.$nextTick(() => {
            this.values = JSON.parse(JSON.stringify(this.edit ? this.edit.properties : this.copyValuesCopy));
            if (this.isLanternType) {
              this.lanternObjects = JSON.parse(
                JSON.stringify(
                  this.edit?.metadata?.street_lamp_objects
                    ? this.prepareLanternObjects(this.edit.metadata.street_lamp_objects)
                    : this.copyLanternObjects
                )
              );
            } else {
              this.lanternObjects = undefined;
            }
            this.$refs.form?.resetValidation();
          });
        } else {
          this.values = {};
          this.lanternObjects = undefined;
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.$watch(
      vm => {
        const { dataSource, layer } = vm;
        return { dataSource, layer };
      },
      nV => {
        const { dataSource, layer } = nV;
        if (dataSource && (layer || layer === 0)) {
          this.formSchema = this.getForm(
            this.layers[layer]?.form_schema.elements || {},
            this.layersMetadata[dataSource].attributes_schema.attributes
          );
        } else {
          this.formSchema = [];
        }
      },
      {
        immediate: true,
        deep: true,
      }
    );
    this.$watch(
      vm => {
        const { copy = {}, readOnly } = vm;
        const { id: featureId, layerId, dataSource, treeMetadata } = copy;
        return { featureId, layerId, dataSource, treeMetadata, readOnly };
      },
      nV => {
        const { featureId, layerId, dataSource, treeMetadata } = nV;
        this.getCopyProperties(layerId, featureId, dataSource, treeMetadata);
      },
      {
        immediate: true,
        deep: true,
      }
    );
  },
};
</script>
<style lang="scss">
.lantern-border {
  @include border(right, 1px);
}
</style>
